/**
 * A percentage per month, as a line. Clicking anywhere in a month's band selects
 * it. Transplanted from the reference implementation's `components/MonthRateChart`.
 *
 * A line rather than columns because the reader's job is the trend, not comparing
 * magnitudes. One series, so no legend — the card title says what is plotted.
 *
 * The y-axis deliberately does NOT start at zero: these rates sit in a narrow band
 * near 100%, and a 0-based axis flattens every real movement into a straight line.
 * The floor is rounded below the observed minimum and every tick is labelled, so
 * the scale is readable rather than implied. This is the property the acceptance
 * test names, so it is carried over exactly.
 *
 * RFT-only, so it lives here rather than in `shared/` — the shared
 * `MonthColumnChart` is used by both reports; this is not.
 *
 * Two changes from the reference, both to satisfy the portal's stricter lint, and
 * neither visual: non-null assertions on the hovered index become a carried
 * `active` object (as in `MonthColumnChart`), and a `num()` helper wraps the
 * coordinates in the path template, which `restrict-template-expressions` refuses
 * to stringify implicitly.
 */

import { useState } from 'react'

import { monthLabel, monthShort } from '../shared/format'
import { useElementWidth } from '../shared/useElementWidth'

const HEIGHT = 240
const PAD = { top: 18, right: 16, bottom: 34, left: 46 }

export interface RatePoint {
  month: string
  /** Percentage 0-100, or null for a month with no records. */
  value: number | null
  /** Optional numerator/denominator for the tooltip. */
  detail?: string
}

interface Props {
  data: readonly RatePoint[]
  selectedMonth: string | null
  onSelectMonth: (month: string) => void
  emptyLabel?: string
}

/** An axis floor below the data, on a 5% step, leaving room to read the variation. */
function axisFloor(min: number): number {
  const floor = Math.floor((min - 2) / 5) * 5
  return Math.max(0, Math.min(floor, 95))
}

/** Coordinates are numbers; `restrict-template-expressions` refuses to stringify
 *  them implicitly inside a path template, and this is pure noise otherwise. */
function num(value: number): string {
  return value.toString()
}

export function MonthRateChart({
  data,
  selectedMonth,
  onSelectMonth,
  emptyLabel = 'No data for this site.',
}: Props) {
  const { ref, width } = useElementWidth<HTMLDivElement>()
  const [hovered, setHovered] = useState<number | null>(null)

  const points = data.filter((d) => d.value !== null)
  if (points.length === 0) return <div className="state">{emptyLabel}</div>

  const chartWidth = Math.max(width, 320)
  const plotWidth = chartWidth - PAD.left - PAD.right
  const plotHeight = HEIGHT - PAD.top - PAD.bottom

  const min = Math.min(...points.map((d) => d.value as number))
  const floor = axisFloor(min)
  const span = 100 - floor

  const ticks: number[] = []
  const step = span <= 10 ? 2 : span <= 25 ? 5 : 10
  for (let v = floor; v <= 100.001; v += step) ticks.push(Math.round(v * 10) / 10)

  const band = plotWidth / data.length
  const xOf = (i: number) => PAD.left + band * i + band / 2
  const yOf = (v: number) => PAD.top + plotHeight - ((v - floor) / span) * plotHeight

  // Break the line across months that have no value rather than drawing through
  // them, so a gap reads as "no records" and not as a dip to the floor.
  const segments: { month: string; value: number; i: number }[][] = []
  let current: { month: string; value: number; i: number }[] = []
  data.forEach((d, i) => {
    if (d.value === null) {
      if (current.length) segments.push(current)
      current = []
    } else {
      current.push({ month: d.month, value: d.value, i })
    }
  })
  if (current.length) segments.push(current)

  const labelEvery = Math.ceil((data.length * 34) / Math.max(plotWidth, 1))

  // The index is carried alongside the point rather than re-read through a
  // non-null assertion, so the crosshair and the tooltip cannot disagree about
  // which month is hovered.
  const hoveredPoint = hovered === null ? undefined : data[hovered]
  const active =
    hovered === null || hoveredPoint === undefined
      ? null
      : { index: hovered, point: hoveredPoint }

  const selectedIndex = data.findIndex((d) => d.month === selectedMonth)
  const selectedPoint = selectedIndex >= 0 ? data[selectedIndex] : undefined
  const selected =
    selectedPoint === undefined || selectedPoint.value === null
      ? null
      : { index: selectedIndex, point: selectedPoint, value: selectedPoint.value }

  // Markers only where they carry meaning: the hovered month, and the selected
  // one when it is not the hovered one.
  const markers: { month: string; x: number; y: number }[] = []
  if (active !== null && active.point.value !== null) {
    markers.push({
      month: active.point.month,
      x: xOf(active.index),
      y: yOf(active.point.value),
    })
  }
  if (selected !== null && selected.point.month !== active?.point.month) {
    markers.push({ month: selected.point.month, x: xOf(selected.index), y: yOf(selected.value) })
  }

  return (
    <div className="chart-wrap" ref={ref}>
      <svg
        width={chartWidth}
        height={HEIGHT}
        role="img"
        aria-label={points
          .map((d) => `${monthLabel(d.month)}: ${(d.value as number).toFixed(1)}%`)
          .join(', ')}
      >
        {ticks.map((tick) => (
          <g key={tick}>
            <line
              x1={PAD.left}
              x2={chartWidth - PAD.right}
              y1={yOf(tick)}
              y2={yOf(tick)}
              stroke={tick === floor ? 'var(--baseline)' : 'var(--gridline)'}
              strokeWidth={1}
            />
            <text
              x={PAD.left - 8}
              y={yOf(tick) + 4}
              textAnchor="end"
              fontSize={11}
              fill="var(--text-muted)"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {tick}%
            </text>
          </g>
        ))}

        {segments.map((segment) => (
          <path
            key={segment[0]?.month}
            d={segment.map((p, n) => `${n === 0 ? 'M' : 'L'}${num(xOf(p.i))},${num(yOf(p.value))}`).join(' ')}
            fill="none"
            stroke="var(--series-1)"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ))}

        {active !== null && (
          <line
            x1={xOf(active.index)}
            x2={xOf(active.index)}
            y1={PAD.top}
            y2={PAD.top + plotHeight}
            stroke="var(--baseline)"
            strokeWidth={1}
          />
        )}

        {markers.map((m) => (
          <circle
            key={m.month}
            cx={m.x}
            cy={m.y}
            r={4.5}
            fill="var(--series-1)"
            stroke="var(--surface-1)"
            strokeWidth={2}
          />
        ))}

        {selected !== null && (
          <text
            x={xOf(selected.index)}
            y={yOf(selected.value) - 12}
            textAnchor="middle"
            fontSize={12}
            fontWeight={600}
            fill="var(--text-primary)"
          >
            {selected.value.toFixed(1)}%
          </text>
        )}

        {data.map((d, i) => (
          <g key={d.month}>
            <rect
              x={PAD.left + band * i}
              y={PAD.top}
              width={band}
              height={plotHeight}
              fill="transparent"
              className="bar-rect"
              onMouseEnter={() => {
                setHovered(i)
              }}
              onMouseLeave={() => {
                setHovered(null)
              }}
              onClick={() => {
                onSelectMonth(d.month)
              }}
            />
            {(i % labelEvery === 0 || d.month === selectedMonth) && (
              <text
                x={xOf(i)}
                y={HEIGHT - PAD.bottom + 16}
                textAnchor="middle"
                fontSize={11}
                fill={d.month === selectedMonth ? 'var(--text-primary)' : 'var(--text-muted)'}
              >
                {monthShort(d.month)}
              </text>
            )}
            {(i === 0 || d.month.endsWith('-01')) && (
              <text
                x={xOf(i)}
                y={HEIGHT - PAD.bottom + 29}
                textAnchor="middle"
                fontSize={10}
                fill="var(--text-muted)"
              >
                {d.month.slice(0, 4)}
              </text>
            )}
          </g>
        ))}
      </svg>

      {active !== null && (
        <div
          className="tooltip"
          style={{
            left: xOf(active.index),
            top: active.point.value !== null ? yOf(active.point.value) : PAD.top + plotHeight / 2,
          }}
        >
          <div className="tooltip-title">{monthLabel(active.point.month)}</div>
          <div className="tooltip-value">
            {active.point.value === null ? 'No records' : `${active.point.value.toFixed(1)}%`}
          </div>
          {active.point.detail !== undefined && (
            <div className="tooltip-title">{active.point.detail}</div>
          )}
        </div>
      )}
    </div>
  )
}
